import { BarChart2, DollarSign, TrendingUp, Users, type LucideIcon } from 'lucide-react';
import type { AdminMetricsPayload } from '../../lib/apiClient';

/**
 * **Key Metrics** — the first section of the console (spec §4).
 *
 * Five cards, in production's order, with production's labels and production's
 * sub-label wording. Every number is computed server-side by production's own
 * formula; this component formats and nothing else.
 *
 * **The conversion sub-label is reproduced, not corrected.** It counts admin
 * grants and coupon redemptions, so it can read `24 of 126 purchased` while
 * Revenue reads `0 paying customers`. The specification records the number and
 * the formula as behavioural; the wording is listed as an undecided cosmetic
 * defect, and the default for an undecided one is to reproduce it.
 *
 * **A failed read is never rendered as zeros.** The console owns that state, as
 * production does: one load, one error. An operator who read `$0.00` from an
 * unreachable endpoint would conclude nothing had sold.
 */
export function KeyMetrics({ metrics: m }: { metrics: AdminMetricsPayload }) {
  return (
    <section data-testid="admin-key-metrics" className="flex flex-col gap-4">
      {/*
        Production's own section heading, in its own treatment: small, muted,
        uppercase and letter-spaced, unlike the bold titles the sections below
        carry.
      */}
      <h2 className="text-lg font-semibold uppercase tracking-widest text-muted-foreground">
        Key Metrics
      </h2>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card
          testId="metric-total-users"
          label="Total Users"
          value={String(m.totalUsers)}
          icon={Users}
        >
          {`+${m.recentUsers} in last 7 days`}
        </Card>

        {/* The one card production gives no sub-label. */}
        <Card
          testId="metric-assessments"
          label="Assessments"
          value={String(m.totalAssessments)}
          icon={BarChart2}
        />

        <Card
          testId="metric-revenue"
          label="Revenue"
          value={money(m.revenueCents)}
          icon={DollarSign}
        >
          {`${m.payingCustomers} paying customers`}
        </Card>

        {/* Production gives this card and the next one the same icon. */}
        <Card
          testId="metric-report-unlocks"
          label="Report Unlocks"
          value={String(m.totalPurchases)}
          icon={TrendingUp}
        >
          {`${m.payingCustomers} paid, ${m.totalPurchases - m.payingCustomers} free`}
        </Card>

        <Card
          testId="metric-conversion"
          label="Conversion Rate"
          value={`${m.conversionRate}%`}
          icon={TrendingUp}
        >
          {`${m.totalPurchases} of ${m.totalAssessments} purchased`}
        </Card>
      </div>
    </section>
  );
}

/** `$X.XX`. Cents are the transport unit; production divides by 100 to display. */
function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function Card({
  label,
  value,
  testId,
  icon: Icon,
  children,
}: {
  label: string;
  value: string;
  testId: string;
  /** Production's icon for this card, top-right and muted. */
  icon: LucideIcon;
  /** The sub-label. Omitted entirely where production has none. */
  children?: string;
}) {
  return (
    <div
      className="flex flex-col gap-1 rounded-xl border border-border bg-card p-5"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
        {/* Decorative: the label beside it already names the card. */}
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </div>
      <p className="text-3xl font-semibold tabular-nums">{value}</p>
      {children ? <p className="text-sm text-muted-foreground">{children}</p> : null}
    </div>
  );
}
